var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios, {} from "axios";
import { GalaxyApi } from "@/api";
import { prependPath } from "@/utils/redirect";
import { rethrowSimple } from "@/utils/simple-error";
/**
 * Generic json getter
 * @param response
 * @return response.data or throws an error if response status is not 200
 */
function doResponse(response) {
    if (response.status !== 200) {
        throw new Error(response.statusText);
    }
    return response.data;
}
/**
 * Some current endpoints don't accept JSON, so we need to
 * do some massaging to send in old form post data.
 */
function formData(fields = {}) {
    return Object.keys(fields).reduce((result, fieldName) => {
        result.set(fieldName, fields[fieldName]);
        return result;
    }, new FormData());
}
/**
 * Extended history request parameters.
 * Retrieves additional details which are usually more "expensive".
 */
const extendedHistoryParams = {
    view: "summary",
    keys: "size,contents_active,user_id",
};
/**
 * Create a new history, select it as the current history, and return it if successful.
 * @return the new history or throws an error if new history creation fails
 */
export function createAndSelectNewHistory() {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const url = "history/create_new_current";
        const response = yield axios.get(prependPath(url));
        const newHistoryId = ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.id) || null;
        if (!newHistoryId) {
            throw new Error("failed to create and select new history");
        }
        return doResponse(response);
    });
}
/**
 * Get current history from server and return it.
 * @param since timestamp to get histories since
 * @return the current history
 */
export function getCurrentHistoryFromServer(since = undefined) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = "history/current_history_json";
        const response = yield axios.get(prependPath(url), { params: { since: since } });
        return doResponse(response);
    });
}
/**
 * Set current history on server and return it.
 * @param historyId Encoded history id
 * @return the current history
 */
export function setCurrentHistoryOnServer(historyId) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = "history/set_as_current";
        const response = yield axios.get(prependPath(url), { params: { id: historyId } });
        return doResponse(response);
    });
}
/**
 * Get list of histories from server and return them.
 * @param offset to start from (default = 0)
 * @param limit of histories to load (default = null; in which case no limit)
 * @param queryString to append to url in the form `q=filter&qv=val&q=...`
 * @return list of histories
 */
export function getHistoryList(offset = 0, limit = null, queryString = "") {
    return __awaiter(this, void 0, void 0, function* () {
        // TODO: to convert this to openapi-fetch we need to fix the query string handling
        // in the caller code to use the query object instead of a string
        const params = `view=summary&order=update_time&offset=${offset}`;
        let url = `api/histories?${params}`;
        if (limit !== null) {
            url += `&limit=${limit}`;
        }
        if (queryString !== "") {
            url += `&${queryString}`;
        }
        const response = yield axios.get(prependPath(url));
        return doResponse(response);
    });
}
/** Load one history by id */
export function getHistoryByIdFromServer(id) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data, error } = yield GalaxyApi().GET("/api/histories/{history_id}", {
            params: {
                path: { history_id: id },
                query: extendedHistoryParams,
            },
        });
        if (error) {
            rethrowSimple(error);
        }
        // We know that the data is a HistorySummaryExtended because we requested it
        // with the extendedHistoryParams
        return data;
    });
}
/**
 * Set permissions to private for indicated history
 * TODO: rewrite API endpoint for this
 * @param history the history to secure
 * @return the secured history
 */
export function secureHistoryOnServer(history) {
    return __awaiter(this, void 0, void 0, function* () {
        const { id } = history;
        const url = "history/make_private";
        const response = yield axios.post(prependPath(url), formData({ history_id: id }));
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
        return yield getHistoryByIdFromServer(id);
    });
}
/**
 * Update specific fields in history
 * @param historyId the history id to update
 * @param payload fields to update
 * @return the updated history
 */
export function updateHistoryFields(historyId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data, error } = yield GalaxyApi().PUT("/api/histories/{history_id}", {
            params: {
                path: { history_id: historyId },
                query: extendedHistoryParams,
            },
            body: payload,
        });
        if (error) {
            rethrowSimple(error);
        }
        // We know that the data is a HistorySummaryExtended because we requested it
        // with the extendedHistoryParams
        return data;
    });
}
